import { useMemo } from "react";
import { adminApi, type AdminAuditLog } from "@/lib/api";
import { useDataTable } from "@/hooks/use-data-table";
import { DataTable } from "@/components/ui";
import { Button } from "@/components/ui/button";
import type { ColumnDef } from "@/components/ui";
import { Download } from "lucide-react";
import { downloadCsv } from "@/lib/utils";
import { PageMeta, ADMIN_PAGE_META } from "@/lib/seo";

const ACTION_OPTIONS = [
  { value: "", label: "Alla åtgärder" },
  { value: "admin.user.update", label: "Användare uppdaterad" },
  { value: "admin.user.soft_delete", label: "Användare borttagen" },
  { value: "admin.user.restore", label: "Användare återställd" },
  { value: "admin.user.reset_password", label: "Lösenord återställt" },
  { value: "admin.user.assign_role", label: "Roll tilldelad" },
  { value: "admin.user.remove_role", label: "Roll borttagen" },
  { value: "admin.company.update", label: "Företag uppdaterat" },
  { value: "admin.company.soft_delete", label: "Företag borttaget" },
  { value: "admin.company.restore", label: "Företag återställt" },
  { value: "admin.report.soft_delete", label: "Årsredovisning borttagen" },
  { value: "admin.report.restore", label: "Årsredovisning återställd" },
  { value: "admin.settings.update", label: "Inställning/pris ändrad" },
  { value: "admin.role.create", label: "Roll skapad" },
  { value: "admin.role.update", label: "Roll uppdaterad" },
  { value: "admin.role.soft_delete", label: "Roll borttagen" },
  { value: "admin.role.restore", label: "Roll återställd" },
];

function getActionBadgeStyle(action: string) {
  const isDestructive = action.includes("delete") || action.includes("remove");
  const isCreate = action.includes("create") || action.includes("restore");
  const isUpdate = action.includes("update") || action.includes("edit");

  if (isDestructive) return "bg-red-100 text-red-700";
  if (isCreate) return "bg-green-100 text-green-700";
  if (isUpdate) return "bg-blue-100 text-blue-700";
  return "bg-gray-100 text-gray-700";
}

export function AdminAuditLogs() {
  const {
    data,
    isLoading,
    search,
    setSearch,
    filters,
    setFilter,
    pagination,
    setPage,
    setPageSize,
    sortColumnId,
    sortDirection,
    handleSort,
    resetFilters,
  } = useDataTable<AdminAuditLog, { action: string; createdAt: string; ipAddress: string }>({
    fetcher: (params) =>
      adminApi.listAuditLogs({
        page: params.page,
        pageSize: params.pageSize,
        search: params.search,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        action: params.action || undefined,
        ipAddress: params.ipAddress || undefined,
        createdAfter: params.createdAt || undefined,
        createdBefore: params.createdAt || undefined,
      }),
    pageSize: 25,
    initialFilters: { action: "", createdAt: "", ipAddress: "" },
    sortKeyMap: {
      action: "action",
      user: "userEmail",
      createdAt: "createdAt",
      ipAddress: "ipAddress",
    },
  });

  const columns: ColumnDef<AdminAuditLog>[] = useMemo(
    () => [
      {
        id: "action",
        header: "Åtgärd",
        sortable: true,
        sortAccessor: (row) => row.action,
        filter: {
          type: "select" as const,
          value: filters.action,
          onChange: (v: string) => setFilter("action", v),
          options: ACTION_OPTIONS,
        },
        cell: (row) => (
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${getActionBadgeStyle(row.action)}`}
            >
              {row.action}
            </span>
            {row.entityType && (
              <span className="text-xs text-gray-400">
                {row.entityType}
                {row.entityId && ` (${row.entityId.slice(0, 8)}...)`}
              </span>
            )}
          </div>
        ),
      },
      {
        id: "user",
        header: "Användare",
        sortable: true,
        sortAccessor: (row) => row.userEmail || "",
        filter: {
          type: "text" as const,
          value: search,
          onChange: setSearch,
          placeholder: "Sök...",
        },
        cell: (row) => (
          <span className="text-sm text-gray-600">
            {row.userEmail || row.userId?.slice(0, 8) || "System"}
          </span>
        ),
      },
      {
        id: "createdAt",
        header: "Tidpunkt",
        hideBelow: "sm",
        sortable: true,
        sortAccessor: (row) => new Date(row.createdAt),
        filter: {
          type: "date" as const,
          value: filters.createdAt,
          onChange: (v: string) => setFilter("createdAt", v),
        },
        cell: (row) => (
          <span className="text-sm text-gray-500">
            {new Date(row.createdAt).toLocaleString("sv-SE")}
          </span>
        ),
      },
      {
        id: "ipAddress",
        header: "IP-adress",
        hideBelow: "lg",
        sortable: true,
        sortAccessor: (row) => row.ipAddress || "",
        filter: {
          type: "text" as const,
          value: filters.ipAddress,
          onChange: (v: string) => setFilter("ipAddress", v),
          placeholder: "Sök IP...",
        },
        cell: (row) => (
          <span className="text-sm text-gray-400">
            {row.ipAddress || "-"}
          </span>
        ),
      },
    ],
    [search, setSearch, filters.action, filters.createdAt, filters.ipAddress, setFilter]
  );

  const expandedContent = (log: AdminAuditLog) => {
    if (!log.oldValues && !log.newValues) return null;

    return (
      <div className="bg-gray-50 rounded-lg p-3 space-y-2">
        {!!log.oldValues && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">
              Tidigare värden:
            </p>
            <pre className="text-xs text-gray-700 overflow-x-auto">
              {JSON.stringify(log.oldValues, null, 2)}
            </pre>
          </div>
        )}
        {!!log.newValues && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">
              Nya värden:
            </p>
            <pre className="text-xs text-gray-700 overflow-x-auto">
              {JSON.stringify(log.newValues, null, 2)}
            </pre>
          </div>
        )}
        {log.userAgent && (
          <p className="text-xs text-gray-400 mt-2 truncate">
            User-Agent: {log.userAgent}
          </p>
        )}
      </div>
    );
  };

  return (
    <>
      <PageMeta title={ADMIN_PAGE_META.auditLog.title} noIndex />
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Systemloggar</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
              Audit trail av alla administrativa åtgärder
            </p>
          </div>
        <Button
          className="w-full sm:w-auto"
          variant="outline"
          size="sm"
          onClick={() =>
            downloadCsv(
              data,
              [
                { header: "ID", accessor: (r) => r.id },
                { header: "Åtgärd", accessor: (r) => r.action },
                { header: "Användare", accessor: (r) => r.userEmail || r.userId || "System" },
                { header: "Entitetstyp", accessor: (r) => r.entityType },
                { header: "Entitets-ID", accessor: (r) => r.entityId },
                { header: "IP-adress", accessor: (r) => r.ipAddress },
                { header: "Tidpunkt", accessor: (r) => new Date(r.createdAt).toLocaleString("sv-SE") },
              ],
              `systemloggar-${new Date().toISOString().slice(0, 10)}.csv`
            )
          }
          disabled={data.length === 0}
        >
          <Download size={14} />
          Exportera CSV
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={data}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        emptyMessage="Inga loggposter hittades"
        pagination={pagination}
        onPageChange={setPage}
        pageSizeOptions={[25, 50, 100, 250]}
        onPageSizeChange={setPageSize}
        expandedContent={expandedContent}
        serverSortColumnId={sortColumnId}
        serverSortDirection={sortDirection}
        onSort={handleSort}
        onResetFilters={resetFilters}
      />
    </div>
    </>
  );
}
